// src/i18n.js
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

/**
 * Every namespace actually used anywhere in the app via useTranslation('x'),
 * so i18next fetches all of them together during init() instead of lazily
 * the first time each page/component mounts.
 *
 * Why this matters: react-snap prerenders every route and waits for it to
 * fully settle - including any on-demand namespace fetch - before snapshotting,
 * so the static HTML always has fully-translated content. But react: {
 * useSuspense: true } means a component whose namespace hasn't loaded yet
 * suspends on its very first render, and there's no <Suspense> boundary
 * anywhere in this app to catch that. A namespace that's only ever fetched
 * lazily (the first time its component renders) can never be ready during
 * that first synchronous render - fetches never resolve within the same
 * render pass, even for a fast local file - so hydration was GUARANTEED to
 * mismatch on every page load, every time, since footer.jsx (which renders
 * on every single page) uses its own 'footer' namespace that was never
 * preloaded. That's what was throwing "Minified React error #418" in
 * production. Preloading every namespace up front, combined with index.js
 * waiting for the init promise below before ever calling hydrateRoot,
 * closes the gap entirely: by the time hydration runs, every t() call
 * already has real data and produces the exact same output the prerendered
 * HTML has baked in.
 */
/**
 * Mirrors withLang.js's getCurrentLang() exactly - matches the URL's lang
 * segment, defaulting to 'en' for "/" or "/404.html" or anything else
 * unmatched.
 *
 * Without this, i18next has no explicit `lng` at init(), so
 * LanguageDetector's `order` decides the STARTING language - and
 * 'navigator' sits before 'htmlTag' in that order, so on a completely
 * fresh browser (no stored preference) it ALWAYS wins, reporting whatever
 * OS/browser language the visitor has configured, completely ignoring which
 * URL they actually requested. On the very first render - the one that has
 * to match the prerendered HTML exactly, or hydration throws - i18n.language
 * was "whatever the visitor's browser reports", not "whatever this page
 * actually is". LanguageRouterWrapper's useEffect does call
 * i18n.changeLanguage(lang) to correct this, but that only runs AFTER the
 * initial render/mount, so it was always one render too late: the
 * prerendered HTML for /hy/... is Armenian, the freshly-hydrating client
 * started out in whatever the browser reports (English, in the overwhelming
 * majority of real visits from this site's audience), and hydration doesn't
 * tolerate a full-page language mismatch. Confirmed directly - every /en/
 * and root "/" page always hydrated cleanly (nothing to mismatch, English
 * is also the fallback), while /hy/, /ru/ and /es/ pages threw "Minified
 * React error #418" on a genuinely fresh load, every single time, in every
 * browser tested. Setting `lng` explicitly here takes priority over
 * LanguageDetector entirely (that's documented i18next behavior - an
 * explicit `lng` skips detection), so the correct language is already in
 * place before the very first render.
 */
const getLangFromUrl = () => {
  const match = window.location.pathname.match(/^\/(en|hy|ru|es)(\/|$)/);
  return match ? match[1] : 'en';
};

const ALL_NAMESPACES = [
  'translation',
  'footer',
  'aboutPage',
  'faq',
  'legalDisclaimer',
  'contactUs',
  'teamPage',
  'accPage',
  'privacyPolicy',
  'servicePage',
  'immigration',
  'lemonLaw',
  'workersCamp',
  'employmentLaw',
  'personalInjury',
  'blogPage',
  'homePage',
  'pageNotFound',
];

const i18nInitPromise = i18n
  .use(HttpBackend) // load translations from /public/locales
  .use(LanguageDetector) // detect user language (cookie, localStorage, navigator, etc.)
  .use(initReactI18next) // pass i18n to react-i18next
  .init({
    lng: getLangFromUrl(),
    fallbackLng: 'en',
    supportedLngs: ['en', 'hy', 'ru', 'es'],
    ns: ALL_NAMESPACES,
    defaultNS: 'translation',
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },
    detection: {
      order: ['querystring', 'localStorage', 'cookie', 'navigator', 'htmlTag'],
      caches: ['localStorage', 'cookie'],
    },
    interpolation: { escapeValue: false }, // React already does XSS protection
    react: { useSuspense: true },
  });

export default i18n;
export { i18nInitPromise };
